import {
    apiErrorMessage,
    toast,
    useObjectColors,
    useSetObjectColor,
    type ColorableType,
    type ColorKey,
} from '@bemor/web-kit';
import {
    Button,
    ColorSwatchPicker,
    ColorTag,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    ColorIcon as Palette,
    Popover,
    PopoverContent,
    PopoverTrigger,
    statusLabel,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

/** Saves an object's color at once, with a toast either way (BM-46 §2.5; the service checks the permission). */
function useSaveColor() {
    const set = useSetObjectColor();
    return (type: ColorableType, id: string, color: ColorKey | null) =>
        set.mutate(
            { type, id, color },
            {
                onSuccess: () =>
                    toast.success(color ? 'Color saved' : 'Color cleared'),
                onError: (e) => toast.error(apiErrorMessage(e)),
            },
        );
}

/** A queue's (or any colorable object's) color: a small button opening the swatches. */
export function ObjectColorButton({
    type,
    id,
    label,
}: {
    type: ColorableType;
    id: string;
    label: string;
}) {
    const { colorOf } = useObjectColors(type);
    const save = useSaveColor();
    const color = colorOf(type, id);
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline" data-testid="object-color-button">
                    <Palette className="size-4" aria-hidden="true" />
                    Color
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto">
                <ColorSwatchPicker
                    label={`${label} color`}
                    value={color}
                    onChange={(next) => save(type, id, next)}
                />
            </PopoverContent>
        </Popover>
    );
}

/** The case kinds with their colors (the kind column and the board by kind), each saved on choosing. */
export function KindColorsDialog({ kinds }: { kinds: readonly string[] }) {
    const [open, setOpen] = useState(false);
    const { colorOf } = useObjectColors('case_kind');
    const save = useSaveColor();
    return (
        <>
            <Button
                variant="outline"
                onClick={() => setOpen(true)}
                data-testid="kind-colors-button"
            >
                <Palette className="size-4" aria-hidden="true" />
                Kind colors
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Case kind colors</DialogTitle>
                    </DialogHeader>
                    <ul className="flex flex-col gap-4">
                        {kinds.map((kind) => (
                            <li key={kind} className="flex flex-col gap-2">
                                <ColorTag color={colorOf('case_kind', kind)}>
                                    {statusLabel(kind)}
                                </ColorTag>
                                <ColorSwatchPicker
                                    label={`${statusLabel(kind)} color`}
                                    value={colorOf('case_kind', kind)}
                                    onChange={(next) =>
                                        save('case_kind', kind, next)
                                    }
                                />
                            </li>
                        ))}
                    </ul>
                </DialogContent>
            </Dialog>
        </>
    );
}
